import { Fragment, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PageTransition, StaggerItem } from '../app/page-transition.js';
import { TableOfContents } from '../../components/ui/table-of-contents.js';
import { showPreferencesModal } from '../../lib/cookie-consent.js';

interface ContentSection {
  id: string;
  title: string;
  body: string[];
  subsections?: ContentSection[];
}

interface PageContent {
  lastUpdated?: string;
  sections: ContentSection[];
}

const LINK_PATTERN = /\{\{link:([^|]+)\|([^}]+)\}\}/g;
const BOLD_PATTERN = /\*\*([^*]+)\*\*/g;

function renderTextSegment(text: string, index: number): ReactNode {
  // Split by link pattern first
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const linkRegex = new RegExp(LINK_PATTERN.source, 'g');

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(renderBold(text.slice(lastIndex, match.index), `pre-${index}-${lastIndex}`));
    }
    const href = match[1] ?? '';
    const label = match[2] ?? '';
    if (href.startsWith('http')) {
      parts.push(
        <a
          key={`link-${index}-${match.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="link-underline-draw text-accent-blue focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {label}
        </a>,
      );
    } else {
      parts.push(
        <Link
          key={`link-${index}-${match.index}`}
          to={href}
          className="link-underline-draw text-accent-blue focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {label}
        </Link>,
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(renderBold(text.slice(lastIndex), `post-${index}-${lastIndex}`));
  }

  return parts.length === 1 ? (
    parts[0]
  ) : (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>{part}</Fragment>
      ))}
    </>
  );
}

function renderBold(text: string, keyPrefix: string): ReactNode {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const boldRegex = new RegExp(BOLD_PATTERN.source, 'g');

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`bold-${keyPrefix}-${match.index}`} className="font-semibold text-primary">
        {match[1]}
      </strong>,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex === 0) return text;
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));

  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>{part}</Fragment>
      ))}
    </>
  );
}

function renderBody(body: string[]) {
  const listItems: string[] = [];
  const elements: ReactNode[] = [];

  function flushList() {
    if (listItems.length > 0) {
      elements.push(
        <ul
          key={`list-${elements.length}`}
          className="list-disc list-inside space-y-1 text-body text-secondary"
        >
          {listItems.map((item, i) => (
            <li key={i}>{renderTextSegment(item, i)}</li>
          ))}
        </ul>,
      );
      listItems.length = 0;
    }
  }

  for (let i = 0; i < body.length; i++) {
    const line = body[i] ?? '';
    if (line.startsWith('- ')) {
      listItems.push(line.slice(2));
    } else {
      flushList();
      elements.push(
        <p key={`p-${i}`} className="text-body text-secondary">
          {renderTextSegment(line, i)}
        </p>,
      );
    }
  }
  flushList();

  return elements;
}

function SectionContent({ section }: { section: ContentSection }) {
  return (
    <section className="scroll-mt-24">
      <h2 id={section.id} className="text-h3 font-semibold text-primary mb-3 scroll-mt-24">
        {section.title}
      </h2>
      <div className="space-y-3 mb-8">{renderBody(section.body)}</div>
      {section.subsections?.map((sub) => (
        <div key={sub.id} className="ml-4 mb-6">
          <h3 id={sub.id} className="text-body font-semibold text-primary mb-2 scroll-mt-24">
            {sub.title}
          </h3>
          <div className="space-y-2">{renderBody(sub.body)}</div>
        </div>
      ))}
    </section>
  );
}

export function LegalPage({ pageKey }: { pageKey: string }) {
  const { t, i18n } = useTranslation('legal');
  const title = t(`pages.${pageKey}`);
  const content = t(`content.${pageKey}`, { returnObjects: true }) as PageContent | string;
  const hasContent = typeof content === 'object' && content?.sections;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} — AgentBrush`;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  if (!hasContent) {
    return (
      <PageTransition>
        <StaggerItem>
          <h1 className="text-h1 font-semibold text-primary mb-6">{title}</h1>
          <p className="text-body text-secondary">{t('pages.comingSoon')}</p>
        </StaggerItem>
      </PageTransition>
    );
  }

  const pageContent = content as PageContent;
  const lastUpdated = pageContent.lastUpdated;

  return (
    <PageTransition>
      <StaggerItem>
        <div className="grid grid-cols-1 standard:grid-cols-[1fr_250px] gap-8">
          <article>
            <h1 className="text-h1 font-semibold text-primary mb-2">{title}</h1>
            {lastUpdated && (
              <p className="text-small text-secondary mb-8">
                {t('lastUpdated', {
                  defaultValue: 'Last updated: {{date}}',
                  date: new Intl.DateTimeFormat(i18n.language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }).format(new Date(lastUpdated)),
                })}
              </p>
            )}
            <div className="space-y-2">
              {pageContent.sections.map((section) => (
                <SectionContent key={section.id} section={section} />
              ))}
            </div>
            {pageKey === 'cookiePolicy' && (
              <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.08)]">
                <button
                  type="button"
                  onClick={() => showPreferencesModal()}
                  className="inline-flex items-center px-4 py-2 text-body font-medium text-primary bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.1)] rounded transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 cursor-pointer"
                >
                  {t('manageCookiePreferences')}
                </button>
              </div>
            )}
          </article>
          <div className="hidden standard:block">
            <TableOfContents />
          </div>
        </div>
      </StaggerItem>
    </PageTransition>
  );
}
